import { useState, type FormEvent } from "react";
import { type Language } from "../../lib/translations";
import { Card, CardTitle, CardDescription, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { InputField } from "./InputField";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { useLocation } from "react-router-dom";

interface ContactFormProps {
  lang: Language;
  t: any;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  topic?: string;
  content?: string;
}

function validateContactForm(
  name: string,
  email: string,
  topic: string,
  content: string,
  t: any
): { errors: ValidationErrors; isValid: boolean } {
  const errors: ValidationErrors = {};
  if (!name.trim()) errors.name = t.requiredField;
  if (!email.trim()) errors.email = t.requiredField;
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = t.invalidEmail;
  if (!topic.trim()) errors.topic = t.requiredField;
  if (!content.trim()) errors.content = t.requiredField;

  return {
    errors,
    isValid: Object.keys(errors).length === 0
  };
}

export function ContactForm({ lang, t }: ContactFormProps) {
  const location = useLocation();
  const state = location.state as { subject?: string } | null;
  const initialSubject = state?.subject || "";
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const topic = formData.get("topic") as string;
    const content = formData.get("content") as string;

    const { errors, isValid } = validateContactForm(name, email, topic, content, t);

    if (!isValid) {
      setFormErrors(errors as any);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, topic, content })
      });

      if (response.ok) {
        toast.success(t.formSuccess);
        (e.target as HTMLFormElement).reset();
        setSent(true);
        setTimeout(() => setSent(false), 4000);
      } else {
        toast.error(t.formError);
      }
    } catch (err) {
      console.error(err);
      toast.error(t.formError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card id="contact-form-card" className="border border-border/40 bg-card/45 backdrop-blur-md p-6 md:p-8 shadow-sm h-full flex flex-col">
      <div className="text-center md:text-left space-y-1 mb-6">
        <CardTitle className="text-2xl font-extrabold text-brand-primary">{t.subtabForm}</CardTitle>
        <CardDescription className="text-xs font-semibold text-muted-foreground">
          {t.formSubtitle}
        </CardDescription>
      </div>

      <CardContent className="p-0">
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <InputField id="name" label={t.formName} error={formErrors.name} placeholder="e.g. John Doe" />
          <InputField id="email" label={t.formEmail} error={formErrors.email} type="email" placeholder="e.g. john@example.com" />
          <InputField id="topic" label={t.formTopic} error={formErrors.topic} defaultValue={initialSubject} key={initialSubject} placeholder="e.g. Project Consultation" />
          <InputField id="content" label={t.formContent} error={formErrors.content} textarea placeholder="Your message content..." />

          <div className="send-btn-container">
            <button
              type="submit"
              disabled={loading}
              className={`h-11 ${sent ? "sent" : ""}`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4.5 w-4.5 mr-2 animate-spin" />
                  <span>{t.formSending}</span>
                </>
              ) : sent ? (
                <>
                  <div className="svg-wrapper-1">
                    <div className="svg-wrapper">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4.5 w-4.5" fill="currentColor">
                        <path fill="none" d="M0 0h24v24H0z" />
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </div>
                  </div>
                  <span>{lang === "tr" ? "Gönderildi!" : "Sent!"}</span>
                </>
              ) : (
                <>
                  <div className="svg-wrapper-1">
                    <div className="svg-wrapper">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4.5 w-4.5" fill="currentColor">
                        <path fill="none" d="M0 0h24v24H0z" />
                        <path d="M1.946 9.315c-.522-.174-.527-.455.01-.634l19.087-6.362c.529-.176.832.12.684.638l-5.454 19.086c-.15.529-.455.547-.679.045L12 14l6-8-8 6-8.054-2.685z" />
                      </svg>
                    </div>
                  </div>
                  <span>{t.formSubmit}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default ContactForm;
